import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Users, Filter, Check, Building2, Plane, Hourglass, Palmtree, CalendarOff, Shield } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";

const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function ymKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function isoOf(dt) { return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`; }
function parseIso(iso) { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); }
function todayIso() { return isoOf(new Date()); }

const CHIP = {
  office: "border-emerald-200 bg-emerald-50 text-emerald-700",
  office_team: "border-blue-200 bg-blue-100 text-blue-700 font-semibold",
  remote_approved: "border-purple-200 bg-purple-100 text-purple-700",
  remote_pending: "border-amber-300 border-dashed bg-amber-50 text-amber-700",
  vacaciones: "border-sky-200 bg-sky-100 text-sky-700",
  ausencia: "border-orange-200 bg-orange-100 text-orange-700",
  guardia_libre: "border-rose-200 bg-rose-100 text-rose-700",
};
const ICON = { office: Building2, office_team: Users, remote_approved: Plane, remote_pending: Hourglass, vacaciones: Palmtree, ausencia: CalendarOff, guardia_libre: Shield };

export default function TeamCalendar() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(null); // null = todos
  const month = ymKey(cursor);

  const usersQ = useQuery({ queryKey: ["rrhh-team-users"], queryFn: async () => (await api.get("/rrhh/team-users")).data });
  const allUsers = useMemo(() => usersQ.data?.items || [], [usersQ.data]);

  const monthQ = useQuery({
    queryKey: ["rrhh-cal-month", month, selected],
    queryFn: async () => (await api.get("/rrhh/calendar/month", { params: { month, user_ids: selected ? selected.join(",") : undefined } })).data,
  });
  const data = monthQ.data;

  const weeks = useMemo(() => {
    if (!data) return [];
    const start = parseIso(data.start), end = parseIso(data.end);
    const out = []; let cur = new Date(start);
    while (cur <= end) {
      const row = [];
      for (let i = 0; i < 7; i++) { row.push(isoOf(cur)); cur.setDate(cur.getDate() + 1); }
      out.push(row);
    }
    return out;
  }, [data]);

  const inMonth = (iso) => data && iso >= data.in_month[0] && iso <= data.in_month[1];
  const move = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const toggleUser = (id) => setSelected((s) => {
    const base = s || allUsers.map((u) => u.id);
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    return next.length === allUsers.length ? null : next;
  });
  const selCount = selected ? selected.length : allUsers.length;

  return (
    <div className="space-y-4" data-testid="rrhh-team-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-heading text-lg font-semibold capitalize">
          <Users className="h-5 w-5 text-primary" />{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
        </h3>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" data-testid="team-cal-filter">
                <Filter className="mr-1.5 h-4 w-4" />{selected ? `${selCount} personas` : "Todos"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="max-h-80 w-64 overflow-y-auto p-2" align="end">
              <button onClick={() => setSelected(null)} className="mb-1 flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted" data-testid="team-cal-filter-all">
                <span className="font-medium">Ver a todos</span>{!selected && <Check className="h-4 w-4 text-primary" />}
              </button>
              <div className="border-t pt-1">
                {allUsers.map((u) => {
                  const on = !selected || selected.includes(u.id);
                  return (
                    <button key={u.id} onClick={() => toggleUser(u.id)} className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted" data-testid={`team-cal-filter-${u.id}`}>
                      <span className="flex items-center gap-2"><UserAvatar name={u.full_name} color={u.avatar_color} size="xs" />{u.full_name}</span>
                      {on && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(-1)} data-testid="team-cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setCursor(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); })}>Hoy</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(1)} data-testid="team-cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {monthQ.isLoading ? <Skeleton className="h-[520px] w-full" /> : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-7 border-b bg-muted/40 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {DOW.map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div>
            {weeks.map((row, ri) => (
              <div key={ri} className="grid grid-cols-7 border-b last:border-b-0">
                {row.map((iso) => {
                  const events = (data?.days || {})[iso] || [];
                  const isToday = iso === todayIso();
                  const dim = !inMonth(iso);
                  const visible = events.slice(0, 3);
                  const extra = events.length - visible.length;
                  return (
                    <div key={iso} className={`min-h-[104px] border-r p-1.5 last:border-r-0 ${dim ? "bg-muted/20" : "bg-card"}`} data-testid={`team-cal-day-${iso}`}>
                      <div className="mb-1 flex justify-end">
                        <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${isToday ? "bg-primary font-bold text-primary-foreground" : dim ? "text-muted-foreground/50" : "text-muted-foreground"}`}>{parseIso(iso).getDate()}</span>
                      </div>
                      <div className="space-y-1">
                        {visible.map((e, i) => <EventChip key={i} e={e} />)}
                        {extra > 0 && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="w-full rounded px-1.5 py-0.5 text-left text-[11px] font-medium text-muted-foreground hover:bg-muted" data-testid={`team-cal-more-${iso}`}>+{extra} más</button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-2" align="start">
                              <p className="px-1 pb-1.5 text-xs font-semibold capitalize">{DOW[(parseIso(iso).getDay() + 6) % 7]} {parseIso(iso).getDate()} {MONTHS[parseIso(iso).getMonth()]}</p>
                              <div className="space-y-1">{events.map((e, i) => <EventChip key={i} e={e} />)}</div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-blue-400" /> Oficina · equipo (martes)</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-400" /> Oficina (persona)</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-purple-400" /> Teletrabajo aprobado</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded border border-dashed border-amber-400 bg-amber-50" /> Teletrabajo solicitado</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-sky-400" /> Vacaciones</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-400" /> Ausencia</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-400" /> Libre por Guardia</span>
      </div>
    </div>
  );
}

function EventChip({ e }) {
  const Icon = ICON[e.type] || Building2;
  return (
    <div className={`flex items-center gap-1 truncate rounded border px-1.5 py-0.5 text-[11px] ${CHIP[e.type] || "border bg-card"}`} title={e.label} data-testid={`team-cal-event-${e.type}`}>
      {e.color ? <UserAvatar name={e.name} color={e.color} size="xs" /> : <Icon className="h-3 w-3 shrink-0" />}
      <span className="truncate">{e.label}</span>
    </div>
  );
}
